import { Pipe, PipeTransform } from '@angular/core';
import { ShipmentConsolStatus } from '../enums/shipment-consol-status.enum';
import { getVintedGoStatusLabels } from '../helpers/vintedgo-status-label.util';

@Pipe({
  name: 'consolShipmentStatusLabel',
  pure: true
})
export class ConsolShipmentStatusLabelPipe implements PipeTransform {

  private readonly statusLabels: Record<number, string> = {
    // --- Consolidation Specific ---
    [ShipmentConsolStatus.CONSOL_CREATED]: 'Consolidation Created',
    [ShipmentConsolStatus.PAYMENT_FAILED]: 'Payment Failed',
    [ShipmentConsolStatus.PAID]: 'Paid',
    [ShipmentConsolStatus.IN_PROGRESS]: 'In Progress',
    
    // --- Core Workflow ---
    [ShipmentConsolStatus.NEW]: 'New',
    [ShipmentConsolStatus.DRAFT]: 'Draft',
    [ShipmentConsolStatus.PENDING]: 'Pending',
    [ShipmentConsolStatus.COLLECTED]: 'Collected',
    [ShipmentConsolStatus.IN_TRANSIT]: 'In Transit',
    [ShipmentConsolStatus.DELIVERED]: 'Delivered',
    [ShipmentConsolStatus.COMPLETED]: 'Completed',
    [ShipmentConsolStatus.CANCELED]: 'Canceled',
    [ShipmentConsolStatus.PENDING_COLLECTION]: 'Pending Collection',
    [ShipmentConsolStatus.PENDING_CUSTOMER_COLLECTION]: 'Pending Customer Collection',

    // --- Customs & Logistics ---
    [ShipmentConsolStatus.CUSTOM_CLEARANCE_PROCESS]: 'Customs Clearance Process',
    [ShipmentConsolStatus.CUSTOMS_CLEARED]: 'Customs Cleared',
    [ShipmentConsolStatus.SHIPMENT_DELIVERED]: 'Shipment Delivered',
    [ShipmentConsolStatus.SHIPMENT_ARRIVED_DESTINATION_PORT]: 'Arrived at Destination Port',
    [ShipmentConsolStatus.SHIPMENT_BACK_2_TRANSIT]: 'Back in Transit',
    [ShipmentConsolStatus.CUSTOM_DELAY]: 'Customs Delay',
    [ShipmentConsolStatus.CUSTOM_CLEARED]: 'Customs Cleared', // Explicitly mapping the duplicate name

    // --- Delays & Exceptions ---
    [ShipmentConsolStatus.DELAYED]: 'Delayed',
    [ShipmentConsolStatus.SHIPMENT_DELAYED]: 'Shipment Delayed',
    [ShipmentConsolStatus.DELAYED_DUE_PAYMENT]: 'Delayed Due to Payment',
    [ShipmentConsolStatus.DELIVERY_ATTEMPT_FAILED]: 'Delivery Attempt Failed',
    [ShipmentConsolStatus.DELIVERY_ADDRESS_CHANGED]: 'Delivery Address Changed',
    [ShipmentConsolStatus.DELIVERY_REFUSED]: 'Delivery Refused',
    [ShipmentConsolStatus.INCORRECT_ADDRESS]: 'Incorrect Address',
    [ShipmentConsolStatus.SHIPMENT_REJECTED]: 'Shipment Rejected',
    [ShipmentConsolStatus.SHIPMENT_RETURNED]: 'Shipment Returned',
    [ShipmentConsolStatus.SHIPMENT_LOST]: 'Shipment Lost',
    [ShipmentConsolStatus.NEW_ADDRESS_RECEIVED]: 'New Address Received',
    [ShipmentConsolStatus.DELIVERY_IN_PROCESS]: 'Delivery in Process',
    [ShipmentConsolStatus.SHIPMENT_CANCELED]: 'Shipment Canceled',
    [ShipmentConsolStatus.PENDING_DELIVERY]: 'Pending Delivery',

    // --- AfterShip Pending (101-106) ---
    [ShipmentConsolStatus.PENDING_001]: 'Pending',
    [ShipmentConsolStatus.PENDING_002]: 'Carrier account not connected',
    [ShipmentConsolStatus.PENDING_003]: 'Label created, no updates yet',
    [ShipmentConsolStatus.PENDING_004]: 'Wrong carrier',
    [ShipmentConsolStatus.PENDING_005]: 'No recent updates',
    [ShipmentConsolStatus.PENDING_006]: 'Unrecognized carrier',

    // --- AfterShip Info Received (111) ---
    [ShipmentConsolStatus.INFORECEIVED_001]: 'Info Received',

    // --- AfterShip In Transit (121-129) ---
    [ShipmentConsolStatus.INTRANSIT_001]: 'In Transit',
    [ShipmentConsolStatus.INTRANSIT_002]: 'Acceptance scan',
    [ShipmentConsolStatus.INTRANSIT_003]: 'Arrival scan',
    [ShipmentConsolStatus.INTRANSIT_004]: 'Arrived at destination country',
    [ShipmentConsolStatus.INTRANSIT_005]: 'Customs clearance completed',
    [ShipmentConsolStatus.INTRANSIT_006]: 'Customs clearance started',
    [ShipmentConsolStatus.INTRANSIT_007]: 'Departure Scan',
    [ShipmentConsolStatus.INTRANSIT_008]: 'Problem resolved',
    [ShipmentConsolStatus.INTRANSIT_009]: 'Forwarded to address',

    // --- AfterShip Out for Delivery (131-134) ---
    [ShipmentConsolStatus.OUTFORDELIVERY_001]: 'Out for Delivery',
    [ShipmentConsolStatus.OUTFORDELIVERY_003]: 'Customer contacted',
    [ShipmentConsolStatus.OUTFORDELIVERY_004]: 'Delivery appointment scheduled',

    // --- AfterShip Attempt Fail (141-143) ---
    [ShipmentConsolStatus.ATTEMPTFAIL_001]: 'Failed Attempt',
    [ShipmentConsolStatus.ATTEMPTFAIL_002]: 'Addressee not available',
    [ShipmentConsolStatus.ATTEMPTFAIL_003]: 'Business Closed',

    // --- AfterShip Delivered (151-154) ---
    [ShipmentConsolStatus.DELIVERED_001]: 'Delivered',
    [ShipmentConsolStatus.DELIVERED_002]: 'Picked up by customer',
    [ShipmentConsolStatus.DELIVERED_003]: 'Signed by customer',
    [ShipmentConsolStatus.DELIVERED_004]: 'Delivered (COD)',

    // --- AfterShip Available for Pickup (161) ---
    [ShipmentConsolStatus.AVAILABLEFORPICKUP_001]: 'Available for pickup',

    // --- AfterShip Exceptions (171-184) ---
    [ShipmentConsolStatus.EXCEPTION_001]: 'Exception',
    [ShipmentConsolStatus.EXCEPTION_002]: 'Customer moved',
    [ShipmentConsolStatus.EXCEPTION_003]: 'Customer refused delivery',
    [ShipmentConsolStatus.EXCEPTION_004]: 'Delayed (Customs)',
    [ShipmentConsolStatus.EXCEPTION_005]: 'Delayed (External)',
    [ShipmentConsolStatus.EXCEPTION_006]: 'Held for payment',
    [ShipmentConsolStatus.EXCEPTION_007]: 'Incorrect Address',
    [ShipmentConsolStatus.EXCEPTION_008]: 'Pick up missed',
    [ShipmentConsolStatus.EXCEPTION_009]: 'Rejected by carrier',
    [ShipmentConsolStatus.EXCEPTION_010]: 'Returning to sender',
    [ShipmentConsolStatus.EXCEPTION_011]: 'Returned to sender',
    [ShipmentConsolStatus.EXCEPTION_012]: 'Shipment damage',
    [ShipmentConsolStatus.EXCEPTION_013]: 'Shipment lost',
    [ShipmentConsolStatus.EXCEPTION_014]: 'Carrier pickup failed',

    // --- AfterShip Expired (191) ---
    [ShipmentConsolStatus.EXPIRED_001]: 'Expired',

    // --- VintedGo statuses (192-219) — labels from Vinted status_code ---
    ...getVintedGoStatusLabels(ShipmentConsolStatus),
  };

  transform(value: number | string | undefined | null): string {
    if (value === undefined || value === null) return '';
    const statusNumber = typeof value === 'string' ? parseInt(value, 10) : value;
    return this.statusLabels[statusNumber] || `Unknown Status (${statusNumber})`;
  }
}